import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Layout } from "@/components/Layout";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Upload, FileCheck, AlertCircle, ShieldCheck, CreditCard,
  Clock, Copy, Loader2, Smartphone, Building2, CheckCircle2, Banknote
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const PRICE_LOCK_SECONDS = 300; // 5 minutes

const formatINR = (n) =>
  (Number(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const normalizePhone = (p) => (p || "").replace(/\D/g, "").slice(-10);
const isValidPhone = (p) => {
  const d = normalizePhone(p);
  if (!/^[6-9]\d{9}$/.test(d)) return false;
  if (/^(\d)\1{9}$/.test(d)) return false; // reject 9999999999, 0000000000, etc.
  return true;
};

const purityLabel = (item) =>
  item.metal_type === "gold" ? `${item.purity || "24K"} Gold` : `${item.purity || "999"} Silver`;
const hallmarkLabel = (item) =>
  item.metal_type === "gold" ? "BIS Hallmarked • 999.9 Fine" : "999 Fine Silver • Assay Certified";

const focusRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] focus-visible:ring-offset-2 focus-visible:ring-offset-[#050505]";

const CheckoutPage = () => {
  const { user, fetchUser } = useAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState(1);

  // KYC
  const [kycData, setKycData] = useState({ documentType: "aadhaar", documentNumber: "", file: null });
  const [kycUploading, setKycUploading] = useState(false);

  // Shipping
  const [shippingData, setShippingData] = useState({
    address: "", city: "", state: "", pincode: "",
    phone: user?.phone || "",
    affiliateCode: localStorage.getItem("am_affiliate_ref") || "",
  });
  const [errors, setErrors] = useState({});
  const [affiliateValid, setAffiliateValid] = useState(null);
  const [affiliateName, setAffiliateName] = useState("");

  // Payment
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("upi");
  const [utr, setUtr] = useState("");
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [paymentError, setPaymentError] = useState("");

  // Price lock countdown
  const [lockSeconds, setLockSeconds] = useState(PRICE_LOCK_SECONDS);

  useEffect(() => {
    fetchCart();
    if (user?.kyc_status === "verified") setStep(2);
    const storedRef = localStorage.getItem("am_affiliate_ref");
    if (storedRef) validateAffiliateCode(storedRef);
  }, [user]);

  useEffect(() => {
    axios.get(`${API}/payment/config`).then((r) => {
      setPaymentConfig(r.data);
      setPaymentMethod(r.data.razorpay_enabled ? "razorpay" : r.data.upi_enabled ? "upi" : "bank");
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setLockSeconds((prev) => {
        if (prev <= 1) {
          fetchCart();
          toast.info("Prices refreshed to the latest live rate");
          return PRICE_LOCK_SECONDS;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const validateAffiliateCode = async (code) => {
    if (!code) { setAffiliateValid(null); setAffiliateName(""); return; }
    try {
      const response = await axios.get(`${API}/affiliate/validate/${code}`);
      setAffiliateValid(true);
      setAffiliateName(response.data.affiliate_name);
    } catch {
      setAffiliateValid(false);
      setAffiliateName("");
    }
  };

  const handleAffiliateCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setShippingData({ ...shippingData, affiliateCode: code });
    if (code.length >= 6) validateAffiliateCode(code);
    else { setAffiliateValid(null); setAffiliateName(""); }
  };

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`);
      if (response.data.items.length === 0) {
        toast.error("Your cart is empty");
        navigate("/cart");
        return;
      }
      setCart(response.data);
    } catch (error) {
      console.error("Failed to fetch cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleKYCUpload = async () => {
    if (!kycData.documentNumber || !kycData.file) {
      toast.error("Please fill in all KYC details");
      return;
    }
    setKycUploading(true);
    const formData = new FormData();
    formData.append("document_type", kycData.documentType);
    formData.append("document_number", kycData.documentNumber);
    formData.append("file", kycData.file);
    try {
      await axios.post(`${API}/kyc/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("KYC document uploaded! Proceeding to shipping...");
      await fetchUser();
      setStep(2);
    } catch {
      toast.error("Failed to upload KYC document");
    } finally {
      setKycUploading(false);
    }
  };

  const validateShipping = () => {
    const e = {};
    if (!shippingData.address || shippingData.address.trim().length < 5)
      e.address = "Please enter your complete address (house no., street, area)";
    if (!shippingData.city || shippingData.city.trim().length < 2) e.city = "Please enter your city";
    if (!shippingData.state || shippingData.state.trim().length < 2) e.state = "Please enter your state";
    if (!/^\d{6}$/.test(shippingData.pincode)) e.pincode = "Enter a valid 6-digit PIN code";
    else if (shippingData.pincode === "000000") e.pincode = "Please enter a real PIN code";
    if (!isValidPhone(shippingData.phone))
      e.phone = "Enter a valid 10-digit mobile number starting with 6-9";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleShippingSubmit = () => {
    if (!validateShipping()) {
      toast.error("Please correct the highlighted fields");
      return;
    }
    setStep(3);
  };

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const createOrder = async () => {
    const orderResponse = await axios.post(`${API}/orders/create`, {
      shipping_address: shippingData.address,
      shipping_city: shippingData.city,
      shipping_state: shippingData.state,
      shipping_pincode: shippingData.pincode,
      phone: normalizePhone(shippingData.phone),
      affiliate_code: shippingData.affiliateCode || null,
    });
    return orderResponse.data;
  };

  const handleRazorpayPayment = async () => {
    setProcessing(true);
    setPaymentError("");
    try {
      const { order_id, total, razorpay_order_id, razorpay_key_id } = await createOrder();
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        setPaymentError("We couldn't load the payment gateway. Check your connection and try again.");
        return;
      }
      const options = {
        key: razorpay_key_id,
        amount: Math.round(total * 100),
        currency: "INR",
        name: "AM GOLD",
        description: "Premium Gold & Silver Coins",
        order_id: razorpay_order_id,
        handler: async (response) => {
          try {
            await axios.post(`${API}/orders/${order_id}/verify-payment`, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Payment successful! Order confirmed.");
            navigate("/dashboard");
          } catch {
            setPaymentError("Payment was received but verification failed. Please contact us with your payment ID.");
          }
        },
        modal: { ondismiss: () => setProcessing(false) },
        prefill: { name: user?.name, email: user?.email, contact: normalizePhone(shippingData.phone) },
        theme: { color: "#D4AF37" },
      };
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      setPaymentError(error.response?.data?.detail || "We couldn't start your payment. Please try again in a moment.");
    } finally {
      setProcessing(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!utr || utr.trim().length < 6) {
      setPaymentError("Please enter a valid UTR / transaction reference number (at least 6 characters).");
      return;
    }
    setProcessing(true);
    setPaymentError("");
    try {
      const { order_id } = await createOrder();
      const fd = new FormData();
      fd.append("method", paymentMethod);
      fd.append("reference", utr.trim());
      if (paymentScreenshot) fd.append("file", paymentScreenshot);
      await axios.post(`${API}/orders/${order_id}/manual-payment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Payment details submitted! Your order is pending verification.");
      navigate("/dashboard");
    } catch (error) {
      setPaymentError(error.response?.data?.detail || "We couldn't submit your payment details. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard?.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Couldn't copy. Please copy manually.")
    );
  };

  const mm = String(Math.floor(lockSeconds / 60)).padStart(2, "0");
  const ss = String(lockSeconds % 60).padStart(2, "0");
  const gstPct = Number(((cart.gst_rate || 0.03) * 100).toFixed(2));

  if (loading) {
    return (
      <Layout>
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="animate-pulse space-y-4">
            <div className="bg-[#121212] h-48" />
            <div className="bg-[#121212] h-48" />
          </div>
        </div>
      </Layout>
    );
  }

  const renderError = (field) =>
    errors[field] ? (
      <p className="text-xs text-[#FF6B6B] mt-1" role="alert" data-testid={`error-${field}`}>
        {errors[field]}
      </p>
    ) : null;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12" data-testid="checkout-page">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-8">Checkout</h1>

        {/* Progress Steps */}
        <div className="flex items-center gap-2 sm:gap-4 mb-10 sm:mb-12" data-testid="checkout-steps">
          {[{ n: 1, label: "KYC Verification" }, { n: 2, label: "Shipping" }, { n: 3, label: "Payment" }].map((s, i) => (
            <div key={s.n} className="flex items-center gap-2 sm:gap-4 flex-1 last:flex-none">
              <div className={`flex items-center gap-2 ${step >= s.n ? "text-[#D4AF37]" : "text-[#8A8A8A]"}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= s.n ? "bg-[#D4AF37] text-black" : "bg-[#121212] text-[#8A8A8A]"}`}>
                  {s.n}
                </div>
                <span className="hidden sm:inline">{s.label}</span>
              </div>
              {i < 2 && <div className="flex-1 h-px bg-[rgba(255,255,255,0.12)]" />}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            {/* Step 1: KYC */}
            {step === 1 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass p-6 sm:p-8" data-testid="kyc-form">
                <div className="flex items-center gap-3 mb-6">
                  <ShieldCheck className="w-6 h-6 text-[#D4AF37]" aria-hidden="true" />
                  <h2 className="text-xl font-semibold text-white">KYC Verification</h2>
                </div>
                <div className="flex items-start gap-3 mb-6 p-4 bg-[rgba(212,175,55,0.1)] border border-[rgba(212,175,55,0.25)]">
                  <AlertCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <p className="text-sm text-[#C5C5C5]">
                    As per Indian bullion regulations, we require a valid government ID (Aadhaar or PAN) for all precious metal purchases.
                  </p>
                </div>
                <div className="space-y-6">
                  <div>
                    <Label>Document Type</Label>
                    <Select value={kycData.documentType} onValueChange={(value) => setKycData({ ...kycData, documentType: value })}>
                      <SelectTrigger className="bg-[#121212] border-[rgba(255,255,255,0.1)]" data-testid="kyc-doc-type"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[rgba(255,255,255,0.1)]">
                        <SelectItem value="aadhaar">Aadhaar Card</SelectItem>
                        <SelectItem value="pan">PAN Card</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{kycData.documentType === "aadhaar" ? "Aadhaar Number" : "PAN Number"}</Label>
                    <Input
                      type="text"
                      placeholder={kycData.documentType === "aadhaar" ? "XXXX XXXX XXXX" : "ABCDE1234F"}
                      value={kycData.documentNumber}
                      onChange={(e) => setKycData({ ...kycData, documentNumber: e.target.value })}
                      className="bg-[#121212] border-[rgba(255,255,255,0.1)]"
                      data-testid="kyc-doc-number"
                    />
                  </div>
                  <div>
                    <Label>Upload Document</Label>
                    <div className="upload-zone" data-testid="kyc-upload-zone">
                      <input type="file" accept="image/*,.pdf" onChange={(e) => setKycData({ ...kycData, file: e.target.files[0] })} className="hidden" id="kyc-file" />
                      <label htmlFor="kyc-file" className="cursor-pointer">
                        {kycData.file ? (
                          <div className="flex items-center justify-center gap-2 text-[#00E676]">
                            <FileCheck className="w-6 h-6" aria-hidden="true" /><span>{kycData.file.name}</span>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-2 text-[#A3A3A3]">
                            <Upload className="w-8 h-8" aria-hidden="true" />
                            <span>Click to upload or drag and drop</span>
                            <span className="text-xs">PNG, JPG or PDF (max 5MB)</span>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>
                  <Button onClick={handleKYCUpload} disabled={kycUploading} className={`w-full btn-gold ${focusRing}`} data-testid="kyc-submit-btn">
                    {kycUploading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Continue to Shipping"}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Shipping */}
            {step === 2 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass p-6 sm:p-8" data-testid="shipping-form">
                <h2 className="text-xl font-semibold text-white mb-6">Shipping Details</h2>
                <div className="space-y-5">
                  <div>
                    <Label>Full Address</Label>
                    <Input type="text" placeholder="House/Flat No., Street, Area" value={shippingData.address}
                      onChange={(e) => setShippingData({ ...shippingData, address: e.target.value })}
                      className={`bg-[#121212] border-[rgba(255,255,255,0.1)] ${errors.address ? "border-[#FF6B6B]" : ""}`}
                      aria-invalid={!!errors.address} data-testid="shipping-address" />
                    {renderError("address")}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>City</Label>
                      <Input type="text" placeholder="City" value={shippingData.city}
                        onChange={(e) => setShippingData({ ...shippingData, city: e.target.value })}
                        className={`bg-[#121212] border-[rgba(255,255,255,0.1)] ${errors.city ? "border-[#FF6B6B]" : ""}`}
                        aria-invalid={!!errors.city} data-testid="shipping-city" />
                      {renderError("city")}
                    </div>
                    <div>
                      <Label>State</Label>
                      <Input type="text" placeholder="State" value={shippingData.state}
                        onChange={(e) => setShippingData({ ...shippingData, state: e.target.value })}
                        className={`bg-[#121212] border-[rgba(255,255,255,0.1)] ${errors.state ? "border-[#FF6B6B]" : ""}`}
                        aria-invalid={!!errors.state} data-testid="shipping-state" />
                      {renderError("state")}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>PIN Code</Label>
                      <Input type="text" inputMode="numeric" maxLength={6} placeholder="6-digit PIN" value={shippingData.pincode}
                        onChange={(e) => setShippingData({ ...shippingData, pincode: e.target.value.replace(/\D/g, "") })}
                        className={`bg-[#121212] border-[rgba(255,255,255,0.1)] ${errors.pincode ? "border-[#FF6B6B]" : ""}`}
                        aria-invalid={!!errors.pincode} data-testid="shipping-pincode" />
                      {renderError("pincode")}
                    </div>
                    <div>
                      <Label>Mobile Number</Label>
                      <Input type="tel" inputMode="numeric" maxLength={10} placeholder="10-digit mobile" value={shippingData.phone}
                        onChange={(e) => setShippingData({ ...shippingData, phone: e.target.value.replace(/\D/g, "") })}
                        className={`bg-[#121212] border-[rgba(255,255,255,0.1)] ${errors.phone ? "border-[#FF6B6B]" : ""}`}
                        aria-invalid={!!errors.phone} data-testid="shipping-phone" />
                      {renderError("phone")}
                    </div>
                  </div>
                  <div>
                    <Label>Affiliate/Referral Code (Optional)</Label>
                    <div className="relative">
                      <Input type="text" placeholder="Enter code if referred" value={shippingData.affiliateCode}
                        onChange={handleAffiliateCodeChange}
                        className={`bg-[#121212] border-[rgba(255,255,255,0.1)] pr-10 ${affiliateValid === true ? "border-[#00E676]" : affiliateValid === false ? "border-[#FF6B6B]" : ""}`}
                        data-testid="affiliate-code" />
                      {affiliateValid === true && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#00E676]"><FileCheck className="w-4 h-4" /></div>}
                      {affiliateValid === false && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#FF6B6B]"><AlertCircle className="w-4 h-4" /></div>}
                    </div>
                    {affiliateValid === true && affiliateName && <p className="text-xs text-[#00E676] mt-1">Referred by: {affiliateName}</p>}
                    {affiliateValid === false && <p className="text-xs text-[#FF6B6B] mt-1">Invalid affiliate code</p>}
                  </div>
                  <Button onClick={handleShippingSubmit} className={`w-full btn-gold ${focusRing}`} data-testid="shipping-submit-btn">
                    Continue to Payment
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Payment */}
            {step === 3 && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass p-6 sm:p-8" data-testid="payment-form">
                <div className="flex items-center gap-3 mb-6">
                  <CreditCard className="w-6 h-6 text-[#D4AF37]" aria-hidden="true" />
                  <h2 className="text-xl font-semibold text-white">Payment</h2>
                </div>

                <div className="p-4 mb-6 bg-[#0a0a0a] border border-[rgba(255,255,255,0.08)]">
                  <p className="text-sm text-[#A3A3A3] mb-1">Shipping to:</p>
                  <p className="text-white">{shippingData.address}</p>
                  <p className="text-white">{shippingData.city}, {shippingData.state} - {shippingData.pincode}</p>
                  <p className="text-[#A3A3A3]">+91 {normalizePhone(shippingData.phone)}</p>
                </div>

                {/* Payment method selection */}
                <fieldset className="space-y-3 mb-6" data-testid="payment-methods">
                  <legend className="text-sm font-medium text-[#C5C5C5] mb-2">Choose a payment method</legend>

                  {paymentConfig?.razorpay_enabled && (
                    <label className={`flex items-center gap-3 p-4 border cursor-pointer transition-colors ${paymentMethod === "razorpay" ? "border-[#D4AF37] bg-[rgba(212,175,55,0.08)]" : "border-[rgba(255,255,255,0.12)] hover:border-[rgba(255,255,255,0.25)]"}`}>
                      <input type="radio" name="paymethod" value="razorpay" checked={paymentMethod === "razorpay"}
                        onChange={() => { setPaymentMethod("razorpay"); setPaymentError(""); }}
                        className={`accent-[#D4AF37] w-4 h-4 ${focusRing}`} data-testid="method-razorpay" />
                      <CreditCard className="w-5 h-5 text-[#D4AF37]" aria-hidden="true" />
                      <span className="text-white text-sm">Pay Online (Card / UPI / Netbanking)</span>
                    </label>
                  )}

                  {paymentConfig?.upi_enabled && (
                    <label className={`flex items-center gap-3 p-4 border cursor-pointer transition-colors ${paymentMethod === "upi" ? "border-[#D4AF37] bg-[rgba(212,175,55,0.08)]" : "border-[rgba(255,255,255,0.12)] hover:border-[rgba(255,255,255,0.25)]"}`}>
                      <input type="radio" name="paymethod" value="upi" checked={paymentMethod === "upi"}
                        onChange={() => { setPaymentMethod("upi"); setPaymentError(""); }}
                        className={`accent-[#D4AF37] w-4 h-4 ${focusRing}`} data-testid="method-upi" />
                      <Smartphone className="w-5 h-5 text-[#D4AF37]" aria-hidden="true" />
                      <span className="text-white text-sm">Direct UPI (GPay / PhonePe / Paytm)</span>
                    </label>
                  )}

                  <label className={`flex items-center gap-3 p-4 border cursor-pointer transition-colors ${paymentMethod === "bank" ? "border-[#D4AF37] bg-[rgba(212,175,55,0.08)]" : "border-[rgba(255,255,255,0.12)] hover:border-[rgba(255,255,255,0.25)]"}`}>
                    <input type="radio" name="paymethod" value="bank" checked={paymentMethod === "bank"}
                      onChange={() => { setPaymentMethod("bank"); setPaymentError(""); }}
                      className={`accent-[#D4AF37] w-4 h-4 ${focusRing}`} data-testid="method-bank" />
                    <Building2 className="w-5 h-5 text-[#D4AF37]" aria-hidden="true" />
                    <span className="text-white text-sm">Bank Transfer (NEFT / IMPS)</span>
                  </label>
                </fieldset>

                {/* Error banner */}
                {paymentError && (
                  <div className="flex items-start gap-3 p-4 mb-6 bg-[rgba(255,59,48,0.1)] border border-[rgba(255,59,48,0.35)]" role="alert" data-testid="payment-error">
                    <AlertCircle className="w-5 h-5 text-[#FF6B6B] flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <div>
                      <p className="text-sm text-[#FF8A80] font-medium">Payment could not be completed</p>
                      <p className="text-sm text-[#C5C5C5] mt-0.5">{paymentError}</p>
                    </div>
                  </div>
                )}

                {/* Razorpay flow */}
                {paymentMethod === "razorpay" && (
                  <>
                    <Button onClick={handleRazorpayPayment} disabled={processing} className={`w-full btn-gold flex items-center justify-center gap-2 ${focusRing}`} data-testid="pay-now-btn">
                      {processing ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Pay ₹{formatINR(cart.total)} <CreditCard className="w-4 h-4" /></>}
                    </Button>
                    <p className="text-xs text-[#A3A3A3] text-center mt-4">
                      Secured by Razorpay. Your payment information is encrypted.
                    </p>
                  </>
                )}

                {/* Manual UPI / Bank flow */}
                {(paymentMethod === "upi" || paymentMethod === "bank") && (
                  <div className="space-y-5" data-testid="manual-payment-section">
                    <div className="p-4 bg-[#0a0a0a] border border-[rgba(212,175,55,0.3)]">
                      <p className="text-sm text-[#C5C5C5] mb-3">
                        Pay <span className="text-[#D4AF37] font-semibold">₹{formatINR(cart.total)}</span> using the details below, then enter your reference number.
                      </p>

                      {paymentMethod === "upi" ? (
                        <div className="flex items-center justify-between gap-3 p-3 bg-[#121212] border border-[rgba(255,255,255,0.08)]">
                          <div>
                            <p className="text-xs text-[#A3A3A3]">UPI ID</p>
                            <p className="text-white font-medium tracking-wide" data-testid="upi-id">{paymentConfig?.upi_id}</p>
                          </div>
                          <button type="button" onClick={() => copyToClipboard(paymentConfig?.upi_id, "UPI ID")}
                            className={`flex items-center gap-1 text-sm text-[#D4AF37] hover:text-[#F3C846] px-3 py-2 ${focusRing}`} data-testid="copy-upi-btn">
                            <Copy className="w-4 h-4" /> Copy
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2" data-testid="bank-details">
                          {[
                            ["Account Name", paymentConfig?.bank_details?.account_name],
                            ["Account Number", paymentConfig?.bank_details?.account_number],
                            ["IFSC", paymentConfig?.bank_details?.ifsc],
                            ["Bank", paymentConfig?.bank_details?.bank_name],
                          ].map(([label, value]) => (
                            <div key={label} className="flex items-center justify-between gap-3 p-3 bg-[#121212] border border-[rgba(255,255,255,0.08)]">
                              <div>
                                <p className="text-xs text-[#A3A3A3]">{label}</p>
                                <p className="text-white font-medium">{value}</p>
                              </div>
                              <button type="button" onClick={() => copyToClipboard(String(value || ""), label)}
                                className={`flex items-center gap-1 text-sm text-[#D4AF37] hover:text-[#F3C846] px-3 py-2 ${focusRing}`}>
                                <Copy className="w-4 h-4" /> Copy
                              </button>
                            </div>
                          ))}
                          <p className="text-xs text-[#A3A3A3] pt-1">Please verify details before transferring. For NEFT/IMPS only.</p>
                        </div>
                      )}
                    </div>

                    <div>
                      <Label>Transaction Reference / UTR Number *</Label>
                      <Input type="text" placeholder="e.g. 123456789012" value={utr}
                        onChange={(e) => setUtr(e.target.value)} className="bg-[#121212] border-[rgba(255,255,255,0.1)]" data-testid="utr-input" />
                      <p className="text-xs text-[#A3A3A3] mt-1">Enter the 12-digit UTR / reference shown in your UPI or bank app after payment.</p>
                    </div>

                    <div>
                      <Label>Payment Screenshot (optional)</Label>
                      <div className="upload-zone" data-testid="payment-screenshot-zone">
                        <input type="file" accept="image/*,.pdf" onChange={(e) => setPaymentScreenshot(e.target.files[0])} className="hidden" id="pay-file" />
                        <label htmlFor="pay-file" className="cursor-pointer">
                          {paymentScreenshot ? (
                            <div className="flex items-center justify-center gap-2 text-[#00E676]">
                              <FileCheck className="w-5 h-5" /><span>{paymentScreenshot.name}</span>
                            </div>
                          ) : (
                            <div className="flex flex-col items-center gap-1 text-[#A3A3A3]">
                              <Upload className="w-6 h-6" aria-hidden="true" />
                              <span className="text-sm">Upload payment screenshot</span>
                            </div>
                          )}
                        </label>
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-[rgba(212,175,55,0.08)] border border-[rgba(212,175,55,0.25)]">
                      <Banknote className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" aria-hidden="true" />
                      <p className="text-sm text-[#C5C5C5]">
                        After submitting, your order status will be <span className="text-[#D4AF37] font-medium">Pending verification</span>. We verify payments {paymentConfig?.verification_time || "within a few working hours"} and confirm your order by email.
                      </p>
                    </div>

                    <Button onClick={handleManualSubmit} disabled={processing} className={`w-full btn-gold flex items-center justify-center gap-2 ${focusRing}`} data-testid="submit-manual-payment-btn">
                      {processing ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-4 h-4" /> I've Paid — Submit for Verification</>}
                    </Button>
                  </div>
                )}
              </motion.div>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-[#121212] border border-[rgba(255,255,255,0.08)] p-6 lg:sticky lg:top-24" data-testid="checkout-summary">
              {/* Price lock notice */}
              <div className="flex items-center gap-2 p-3 mb-5 bg-[rgba(212,175,55,0.08)] border border-[rgba(212,175,55,0.25)]" data-testid="price-lock-notice">
                <Clock className="w-4 h-4 text-[#D4AF37] flex-shrink-0" aria-hidden="true" />
                <p className="text-xs text-[#D4AF37]">
                  Price locked for <span className="font-semibold tabular-nums" data-testid="price-lock-timer">{mm}:{ss}</span>
                  <span className="text-[#C5C5C5]"> • refreshes at live rate</span>
                </p>
              </div>

              <h2 className="text-lg font-semibold text-white mb-5">Order Summary</h2>

              <div className="space-y-4 mb-6">
                {cart.items.map((item) => (
                  <div key={item.id} className="flex gap-3 pb-4 border-b border-[rgba(255,255,255,0.06)] last:border-0 last:pb-0" data-testid="summary-item">
                    <img src={item.image_url} alt={`${item.name} (${purityLabel(item)})`} className="w-16 h-16 object-cover bg-[#0a0a0a] flex-shrink-0 rounded" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white leading-snug">{item.name}</p>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-xs text-[#B0B0B0]">
                        <span>{item.weight} {item.weight_unit}</span>
                        <span aria-hidden="true">•</span>
                        <span>{purityLabel(item)}</span>
                      </div>
                      <p className="text-xs text-[#9A9A9A] mt-0.5">{hallmarkLabel(item)}</p>
                      <p className="text-xs text-[#9A9A9A]">Live rate: ₹{formatINR(item.base_metal_rate)}/g</p>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-xs text-[#B0B0B0]">Qty: {item.quantity}</span>
                        <span className="text-sm text-[#D4AF37]">₹{formatINR(item.item_total)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-2 pt-4 border-t border-[rgba(255,255,255,0.08)]">
                <div className="flex justify-between text-sm">
                  <span className="text-[#B0B0B0]">Subtotal (metal value)</span>
                  <span className="text-white tabular-nums">₹{formatINR(cart.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#B0B0B0]">Making Charges</span>
                  <span className="text-white tabular-nums">₹{formatINR(cart.making_charges)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#B0B0B0]">GST ({gstPct}%)</span>
                  <span className="text-white tabular-nums">₹{formatINR(cart.gst)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#B0B0B0]">Shipping</span>
                  <span className="text-[#00E676] font-medium">FREE</span>
                </div>
                <div className="flex justify-between text-lg font-semibold pt-2 mt-1 border-t border-[rgba(255,255,255,0.08)]">
                  <span className="text-white">Total</span>
                  <span className="text-[#D4AF37] tabular-nums">₹{formatINR(cart.total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CheckoutPage;
